import React, { useEffect, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { PaginationTypes } from "@Shared/Pagination-Types";
import CustomEmptyState from "../../../components/empty-state/CustomEmptyState";
import { CUSTOM_EMPTY_STATE_KEY } from "../../../components/empty-state/custom-templates";
import { searchSpaces } from "../../../api/space-api";
import { useSpacesStore } from "../../../stores/use-space-store";
import { mapSpace } from "../useSpaces";
import SpacesPreview from "../preview/SpacesPreview";

function DynamicSpaces({ mode }) {
  const [page, setPage] = useState(0);
  const [atlassianContext, setAtlassianContext] = useState();

  const { config, selectedTab } = useSpacesStore((state) => ({
    config: state.data.config,
    selectedTab: state.getSelectedTab(),
  }));

  const isCosmosInstalled = useOpticsStore((state) => state.isCosmosInstalled);

  const queryClient = useQueryClient();

  useEffect(() => {
    async function fetchData() {
      const context = await window.AP.context.getContext();
      setAtlassianContext(context);
    }
    fetchData();
  }, []);

  const { tabs, variant, pagination, size } = config || {};
  const { limit } = tabs[selectedTab] || {};

  const {
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    data,
    error,
    refetch,
  } = useInfiniteQuery(
    [
      "space",
      {
        categories: config.tabs[selectedTab].categories,
        spaceType: config.tabs[selectedTab].spaceType,
        spaceStatus: config.tabs[selectedTab].spaceStatus,
        limit: config.tabs[selectedTab].limit,
      },
    ],
    searchSpaces,
    {
      retry: 0,
      select: (response) => {
        if (!response?.pages?.length) return undefined;

        const { pages } = response;

        const builtData = pages.reduce((acc, page, index) => {
          const body = JSON.parse(page.body);
          const blogs =
            body?.results?.map((space) => mapSpace(space, isCosmosInstalled)) ||
            [];

          return {
            ...acc,
            [index]: blogs,
          };
        }, {});

        return builtData;
      },
      getNextPageParam: (lastPage) => {
        const body = JSON.parse(lastPage.body);

        if (body._links.next) {
          const fullNextUrl = new URL(
            body._links.base.replace("/wiki", "") + body._links.next
          );
          const cursor = fullNextUrl.searchParams.get("cursor");
          return cursor;
        }
        return false;
      },
    }
  );

  useEffect(() => {
    queryClient.resetQueries({ queryKey: "space" });
    setPage(0);
  }, [queryClient, pagination, variant, size, limit, selectedTab]);

  const queryPagination = {
    disabled: config.pagination === PaginationTypes.NONE,
    hasNextPage: hasNextPage || Object.keys(data || {})?.length - 1 > page,
    hasPrevPage: page >= 1,
    onNext: () => {
      if (hasNextPage && Object.keys(data || {})?.length - 1 === page) {
        fetchNextPage();
      }
      if (hasNextPage || Object.keys(data || {})?.length - 1 > page) {
        setPage(page + 1);
      }
    },
    onPrev: () => {
      if (page >= 1) {
        setPage(page - 1);
      }
    },
    page,
    setPage,
  };

  if (!Object.values(data || {})?.[0]?.length) {
    return (
      <CustomEmptyState
        templateKey={CUSTOM_EMPTY_STATE_KEY.NEWS_EMPTY_RESULTS}
      />
    );
  }

  return (
    <SpacesPreview data={data} queryPagination={queryPagination} mode={mode} />
  );
}

export default DynamicSpaces;
